<script setup lang="ts">
import { computed, inject, ref, watch, toRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { useAsyncState } from '@vueuse/core'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Menu from 'primevue/menu'
import Message from 'primevue/message'
import type { MenuItem } from 'primevue/menuitem'
import { useConfirm } from 'primevue/useconfirm'
import LoadingContainer from '@/components/LoadingContainer.vue'
import ErrorContainer from '@/components/ErrorContainer.vue'
import FormTitleHeader from '@/components/FormTitleHeader.vue'
import PressEnterKeyToProceed from '@/components/ui/PressEnterKeyToProceed.vue'
import { getFormat } from '@/api/formatApi'
import { getInstitutionsByFormat, deleteInstitution } from '@/api/formatInstitutionApi'
import { useEntityList } from '@/composables/useEntityList'
import { useFormatChapters } from '@/composables/useFormatChapters'
import { useEnterToProceed } from '@/composables/useEnterToProceed'
import { type StoredFormatInstitution } from '@/types/formatInstitution.ts'
import { getMediaUrl, resolveMediaUrl } from '@/utils/media-helper.ts'
import OwnInstitutionFunctionModal from '@/components/forms/institutions/OwnInstitutionFunctionModal.vue'
import InstitutionEntryModal from '@/components/forms/institutions/InstitutionEntryModal.vue'
import InstitutionFunctionHelp from '@/components/forms/institutions/InstitutionFunctionHelp.vue'
import { isFormatReadOnly } from '@/utils/format-helper.ts'
import { hasOrganizingInstitution } from '@beteiligungsplattform/shared'

const props = defineProps<{
    formatId: string
}>()

const { t } = useTranslation()
const route = useRoute()
const router = useRouter()

const { routeToNextChapter } = useFormatChapters(toRef(props, 'formatId'))
const confirm = useConfirm()
const reloadParentFormat = inject<() => Promise<unknown>>('reloadFormatData')

const {
    items: institutions,
    loading,
    error,
    currentFormatDocumentId,
    loadItems,
} = useEntityList<StoredFormatInstitution>({
    formatDocumentId: props.formatId,
    loadFunction: getInstitutionsByFormat,
    deleteFunction: deleteInstitution,
    translations: {
        deleteConfirmMessage: 'ui.institutions.list.deleteConfirmMessage',
        deleteConfirmHeader: 'ui.institutions.list.deleteConfirmHeader',
        deleteConfirmAccept: 'ui.institutions.list.deleteConfirmAccept',
        deleteConfirmReject: 'ui.institutions.list.deleteConfirmReject',
    },
})

const {
    state: format,
    execute: loadFormat,
    isLoading: formatLoading,
} = useAsyncState(async () => {
    const { data } = await getFormat(currentFormatDocumentId.value, 'draft', {
        company: { populate: ['logo'] },
    })
    return data
}, null)

const ownFunctionModalVisible = ref(false)
const entryModalVisible = ref(false)
const helpModalVisible = ref(false)
const selectedInstitution = ref<StoredFormatInstitution | null>(null)

useEnterToProceed(
    routeToNextChapter,
    () => !ownFunctionModalVisible.value && !entryModalVisible.value && !helpModalVisible.value
)

const getQueryValue = (value: unknown): string | undefined => {
    const queryValue = Array.isArray(value) ? value[0] : value
    return typeof queryValue === 'string' ? queryValue : undefined
}

const clearEditQuery = async () => {
    await router.replace({
        query: {
            ...route.query,
            editOwnInstitution: undefined,
            editInstitution: undefined,
        },
    })
}

const ownOrganizationName = computed(() => {
    const company = format.value?.company
    return company && typeof company === 'object' ? company.name : ''
})

const ownOrganizationLogoUrl = computed<string | null>(() => {
    const company = format.value?.company
    const logo = company && typeof company === 'object' ? company.logo : null
    const url = getMediaUrl(logo)
    return url ? resolveMediaUrl(url) : null
})

const institutionRows = computed(() => [
    { own: true, logoUrl: ownOrganizationLogoUrl.value },
    ...institutions.value.map((institution) => {
        const url = getMediaUrl(institution.logo)
        const logoUrl: string | null = url ? resolveMediaUrl(url) : null
        return { ...institution, logoUrl }
    }),
])

const ownFunctionLabel = computed(() =>
    format.value?.ownInstitutionFunction
        ? t(`ui.institutions.function.${format.value.ownInstitutionFunction}.label`)
        : ''
)

const showsMissingOrganizerHint = computed(
    () => !!format.value && !hasOrganizingInstitution(format.value, institutions.value)
)
const isReadOnly = computed<boolean>((): boolean => {
    if (!format?.value) return true
    return isFormatReadOnly(format.value)
})

watch(
    format,
    (loadedFormat) => {
        if (isFormatReadOnly(loadedFormat)) {
            ownFunctionModalVisible.value = false
            entryModalVisible.value = false
            return
        }

        if (loadedFormat && !loadedFormat.ownInstitutionFunction) {
            ownFunctionModalVisible.value = true
        }
    },
    { immediate: true }
)

watch(
    [() => route.query.editOwnInstitution, () => route.query.editInstitution, institutions],
    ([editOwnInstitution, editInstitution, loadedInstitutions]) => {
        if (isReadOnly.value) {
            void clearEditQuery()
            return
        }

        if (getQueryValue(editOwnInstitution) === 'true') {
            ownFunctionModalVisible.value = true
            void clearEditQuery()
            return
        }

        const institutionId = getQueryValue(editInstitution)
        if (!institutionId) return
        const institution = (loadedInstitutions as StoredFormatInstitution[]).find(
            (item) => item.documentId === institutionId
        )
        if (institution) {
            openEntryModal(institution)
            void clearEditQuery()
        }
    },
    { immediate: true }
)

const menu = ref()

const menuItems = computed<MenuItem[]>(() => [
    {
        label: t('ui.institutions.list.editButton'),
        icon: 'pi pi-pencil',
        command: () => openEntryModal(selectedInstitution.value),
    },
    {
        label: t('ui.institutions.list.deleteButton'),
        icon: 'pi pi-trash',
        command: () =>
            selectedInstitution.value && confirmDeleteInstitution(selectedInstitution.value),
        class: 'text-red-500',
    },
])

function toggleMenu(event: MouseEvent, institution: StoredFormatInstitution) {
    selectedInstitution.value = institution
    menu.value.toggle(event)
}

function openEntryModal(institution: StoredFormatInstitution | null) {
    if (isReadOnly.value) return
    selectedInstitution.value = institution
    entryModalVisible.value = true
}

function confirmDeleteInstitution(institution: StoredFormatInstitution) {
    if (isReadOnly.value) return

    confirm.require({
        message: t('ui.institutions.list.deleteConfirmMessage'),
        header: t('ui.institutions.list.deleteConfirmHeader'),
        acceptLabel: t('ui.institutions.list.deleteConfirmAccept'),
        acceptProps: {
            severity: 'danger',
            outlined: true,
            icon: 'pi pi-trash',
            iconPos: 'right',
        },
        rejectLabel: t('ui.institutions.list.deleteConfirmReject'),
        rejectProps: {
            severity: 'primary',
            outlined: true,
        },
        accept: async () => {
            await deleteInstitution(institution.documentId)
            await loadItems()
            await reloadParentFormat?.()
        },
    })
}

async function handleEntrySaved() {
    await loadItems()
    await reloadParentFormat?.()
}

async function handleOwnFunctionSaved() {
    await loadFormat()
    await reloadParentFormat?.()
}
</script>

<template>
    <div class="py-8 max-w-3xl mx-auto px-4 space-y-6">
        <div class="flex flex-col gap-10">
            <FormTitleHeader
                :title="t('ui.institutions.list.tableTitle')"
                :description="t('ui.institutions.list.description')"
            />

            <LoadingContainer v-if="loading || formatLoading" />
            <ErrorContainer v-else-if="error" :error="error" />

            <template v-else>
                <DataTable
                    :value="institutionRows"
                    data-key="documentId"
                    striped-rows
                    class="rounded-xl overflow-clip"
                    :pt="{ table: { 'aria-label': t('ui.institutions.list.tableTitle') } }"
                >
                    <Column :header="t('ui.institutions.list.functionColumn')">
                        <template #body="{ data }">
                            <Tag
                                :value="
                                    data.own
                                        ? ownFunctionLabel
                                        : t(`ui.institutions.function.${data.function}.label`)
                                "
                                rounded
                                class="chip-primary text-primary font-normal"
                            />
                        </template>
                    </Column>
                    <Column
                        :header="t('ui.institutions.list.nameColumn')"
                        :pt="{ bodyCell: { role: 'rowheader' } }"
                    >
                        <template #body="{ data }">
                            {{ data.own ? ownOrganizationName : data.name }}
                        </template>
                    </Column>
                    <Column :header="t('ui.institutions.list.logoColumn')">
                        <template #body="{ data }">
                            <img
                                v-if="data.logoUrl"
                                :src="data.logoUrl"
                                alt=""
                                class="h-8 max-w-24 object-contain"
                            />
                        </template>
                    </Column>
                    <Column
                        v-if="!isReadOnly"
                        :header="t('ui.institutions.list.rowActionsLabel')"
                        :pt="{ headerCell: { class: 'w-20 text-center' } }"
                    >
                        <template #body="{ data }">
                            <Button
                                v-if="data.own"
                                outlined
                                severity="secondary"
                                size="small"
                                icon="pi pi-pencil"
                                :aria-label="`${t('ui.institutions.list.editButton')}: ${ownOrganizationName}`"
                                data-testid="institutions-own-row-edit-button"
                                @click="ownFunctionModalVisible = true"
                            />
                            <Button
                                v-else
                                outlined
                                severity="secondary"
                                size="small"
                                icon="pi pi-ellipsis-v"
                                :aria-label="`${t('ui.institutions.list.rowActionsLabel')}: ${data.name}`"
                                data-testid="institutions-row-actions-button"
                                @click="toggleMenu($event, data)"
                            />
                        </template>
                    </Column>
                </DataTable>

                <Message
                    v-if="showsMissingOrganizerHint"
                    severity="info"
                    icon="pi pi-exclamation-triangle"
                    :closable="false"
                >
                    {{ t('ui.institutions.list.missingOrganizerHint') }}
                </Message>

                <div class="flex justify-between">
                    <Button
                        v-if="!isReadOnly"
                        outlined
                        icon="pi pi-plus"
                        icon-pos="right"
                        size="large"
                        :label="t('ui.institutions.list.addEntryButton')"
                        data-testid="institutions-add-entry-button"
                        @click="openEntryModal(null)"
                    />
                    <Button
                        text
                        :label="t('ui.institutions.list.functionHelpLink')"
                        data-testid="institutions-function-help-button"
                        @click="helpModalVisible = true"
                    />
                </div>

                <div class="flex justify-start pt-8 items-center gap-6">
                    <Button
                        :label="t('ui.common.next')"
                        size="large"
                        data-testid="institutions-next-button"
                        @click="routeToNextChapter"
                    />
                    <PressEnterKeyToProceed />
                </div>
            </template>
        </div>

        <Menu
            v-if="!isReadOnly"
            ref="menu"
            :model="menuItems"
            :popup="true"
            :aria-label="
                selectedInstitution
                    ? `${t('ui.institutions.list.rowActionsLabel')}: ${selectedInstitution.name}`
                    : t('ui.institutions.list.rowActionsLabel')
            "
        />

        <OwnInstitutionFunctionModal
            v-if="!isReadOnly"
            v-model:visible="ownFunctionModalVisible"
            :format-id="currentFormatDocumentId"
            :current-value="format?.ownInstitutionFunction"
            @saved="handleOwnFunctionSaved"
        />

        <InstitutionEntryModal
            v-if="!isReadOnly"
            v-model:visible="entryModalVisible"
            :format-id="currentFormatDocumentId"
            :institution="selectedInstitution"
            @saved="handleEntrySaved"
        />

        <InstitutionFunctionHelp v-model:visible="helpModalVisible" />
    </div>
</template>
